import { useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Lock } from "lucide-react";

const list = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const arr = (v) => String(v || "").split(",").map((x) => x.trim()).filter(Boolean);

// Professional profile editor. `canLock` = owner/manager (license + verified are regulated claims).
export function ProfileEditor({ pro, canLock, onSaved }) {
  const [f, setF] = useState({ ...pro, specialties: list(pro.specialties), areas: list(pro.areas), languages: list(pro.languages), credentials: list(pro.credentials),
    testimonials: (pro.testimonials || []).map((t) => `${t.quote} — ${t.name}`).join("\n") });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => {
    const body = { ...f, specialties: arr(f.specialties), areas: arr(f.areas), languages: arr(f.languages), credentials: arr(f.credentials),
      testimonials: f.testimonials.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [q, n] = l.split(" — "); return { quote: q, name: n || "" }; }) };
    if (!canLock) { delete body.license; delete body.verified; }
    opApi.put(`/tenant/connect/profiles/${pro.id}`, body).then(() => { toast.success("Profile saved"); onSaved && onSaved(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  const field = (k, label, ph) => <div><Label>{label}</Label><Input className="rounded-none mt-1" placeholder={ph} value={f[k] || ""} onChange={set(k)} data-testid={`profile-${k}`} /></div>;
  return (
    <div className="space-y-3" data-testid="profile-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        {field("name", "Name")}{field("title", "Title", "Realtor®, Coach, Consultant…")}
        {field("phone", "Phone")}{field("email", "Email (lead alerts go here)")}
        {field("headshot", "Headshot image URL", "https://…")}{field("slug", "Profile link", "avery-brooks")}
      </div>
      <div><Label>Bio</Label><Textarea className="rounded-none mt-1" rows={3} value={f.bio || ""} onChange={set("bio")} data-testid="profile-bio" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {field("specialties", "Specialties (comma separated)")}{field("areas", "Service areas — ZIPs or cities")}
        {field("languages", "Languages")}{field("credentials", "Credentials")}
      </div>
      <div><Label>Testimonials <span className="text-muted-foreground font-normal">(one per line: quote — name)</span></Label><Textarea className="rounded-none mt-1" rows={2} value={f.testimonials} onChange={set("testimonials")} data-testid="profile-testimonials" /></div>
      {field("payment_link", "Your payment link for sessions/packages (optional)", "https://square.link/…")}
      <div className="border border-border p-3 space-y-2" data-testid="profile-locked">
        <div className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1"><Lock className="h-3 w-3" /> Regulated details {canLock ? "" : "(set by your owner/manager)"}</div>
        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <div><Label>License number</Label><Input disabled={!canLock} className="rounded-none mt-1" value={f.license || ""} onChange={set("license")} data-testid="profile-license" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch disabled={!canLock} checked={!!f.verified} onCheckedChange={(v) => setF({ ...f, verified: v })} data-testid="profile-verified" /> Verified badge</label>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.published !== false} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="profile-published" /> Show in directory</label>
      <Button className="rounded-none" onClick={save} data-testid="profile-save">Save profile</Button>
    </div>
  );
}
